/**
 * Hook to get event-specific asset overrides
 * Combines current event detection with theme-aware asset selection
 */
import { useMemo } from 'react'
import { useTheme } from '../context/ThemeProvider'
import { getThemeName, getThemeMode } from '../utils/theme-utils'
import { useCurrentEvent, EventType } from './useCurrentEvent'
import { ThemeName, ThemeMode } from '../constants/theme'
import { LoadingBackgroundType } from '../constants/asset'

import { CHRISTMAS_ASSETS } from '../assets/events/christmas'
import { CHINESE_NEW_YEAR_ASSETS } from '../assets/events/chinese-new-year'
import { HALLOWEEN_ASSETS } from '../assets/events/halloween'
import { AUTUMN_LEAVES_ASSETS } from '../assets/events/autumn-leaves'
import { SAKURA_ASSETS } from '../assets/events/sakura'
import { SONGKRAN_ASSETS } from '../assets/events/songkran-festival'
import { LOY_KRATHONG_ASSETS } from '../assets/events/loy-krathong'

interface EventAssets {
  logo: string | null
  loadingBackground: string | null
  loadingBackgrounds: Partial<Record<LoadingBackgroundType, string>> | null
  ribbon: string | null
  abstractBorder: string | null
  cardLantern: string | null // CNY lantern decoration
  cardFlagBanner: string | null // Songkran flag banner decoration
  chineseKnot: string | null
  webLeft: string | null // Halloween web decoration - left
  webRight: string | null // Halloween web decoration - right
  models: Record<string, string> | null
  bannerFlagsEn: Record<string, string> | null
  bannerFlagsTh: Record<string, string> | null
}

type ThemeModeKey = 'light' | 'dark'
type ThemeNameKey = 'gold' | 'blue'

const getEventAssets = (
  event: EventType | null,
  themeName: ThemeName,
  themeMode: ThemeMode,
): EventAssets => {
  if (!event) {
    return {
      logo: null,
      loadingBackground: null,
      loadingBackgrounds: null,
      ribbon: null,
      abstractBorder: null,
      cardLantern: null,
      cardFlagBanner: null,
      chineseKnot: null,
      webLeft: null,
      webRight: null,
      models: null,
      bannerFlagsEn: null,
      bannerFlagsTh: null,
    }
  }

  const modeKey: ThemeModeKey = themeMode === ThemeMode.LIGHT ? 'light' : 'dark'
  const nameKey: ThemeNameKey = themeName === ThemeName.GOLD ? 'gold' : 'blue'

  switch (event) {
    case EventType.CHRISTMAS: {
      const themeAssets = CHRISTMAS_ASSETS[nameKey]
      return {
        logo: themeAssets?.logo?.[modeKey] ?? null,
        loadingBackground: themeAssets?.loadingAssets?.backgrounds?.bg1 ?? null,
        loadingBackgrounds: themeAssets?.loadingAssets?.backgrounds ?? null,
        ribbon: themeAssets?.decorations?.ribbon ?? null,
        abstractBorder: null,
        cardLantern: null,
        cardFlagBanner: null,
        chineseKnot: null,
        webLeft: null,
        webRight: null,
        models: themeAssets?.models ?? null,
        bannerFlagsEn: null,
        bannerFlagsTh: null,
      }
    }
    case EventType.AUTUMN_LEAVES: {
      const themeAssets = AUTUMN_LEAVES_ASSETS[nameKey]
      const bannerFlagsEn =
        nameKey === 'blue'
          ? (AUTUMN_LEAVES_ASSETS[nameKey]?.bannerFlagsEn ?? null)
          : null
      const bannerFlagsTh =
        nameKey === 'blue'
          ? (AUTUMN_LEAVES_ASSETS[nameKey]?.bannerFlagsTh ?? null)
          : null
      const cardLantern =
        nameKey === 'blue'
          ? (AUTUMN_LEAVES_ASSETS[nameKey]?.decorations?.cardLantern ?? null)
          : null

      return {
        logo: themeAssets?.logo?.[modeKey] ?? null,
        loadingBackground: themeAssets?.loadingAssets?.backgrounds?.bg1 ?? null,
        loadingBackgrounds: themeAssets?.loadingAssets?.backgrounds ?? null,
        ribbon: null,
        abstractBorder: themeAssets?.decorations?.abstractBorder ?? null,
        cardLantern,
        cardFlagBanner: null,
        chineseKnot: null,
        webLeft: themeAssets?.decorations?.webLeft ?? null,
        webRight: themeAssets?.decorations?.webRight ?? null,
        models: themeAssets?.models ?? null,
        bannerFlagsEn,
        bannerFlagsTh,
      }
    }
    case EventType.HALLOWEEN: {
      const themeAssets = HALLOWEEN_ASSETS[nameKey]
      const bannerFlagsEn =
        nameKey === 'blue'
          ? ((themeAssets as typeof HALLOWEEN_ASSETS.blue)?.bannerFlagsEn ??
            null)
          : null
      const bannerFlagsTh =
        nameKey === 'blue'
          ? ((themeAssets as typeof HALLOWEEN_ASSETS.blue)?.bannerFlagsTh ??
            null)
          : null
      const cardLantern =
        nameKey === 'blue'
          ? ((themeAssets as typeof HALLOWEEN_ASSETS.blue)?.decorations
              ?.cardLantern ?? null)
          : null

      return {
        logo: themeAssets?.logo?.[modeKey] ?? null,
        loadingBackground: themeAssets?.loadingAssets?.backgrounds?.bg1 ?? null,
        loadingBackgrounds: themeAssets?.loadingAssets?.backgrounds ?? null,
        ribbon: null,
        abstractBorder: themeAssets?.decorations?.abstractBorder ?? null,
        cardLantern,
        cardFlagBanner: null,
        chineseKnot: null,
        webLeft: themeAssets?.decorations?.webLeft ?? null,
        webRight: themeAssets?.decorations?.webRight ?? null,
        models: themeAssets?.models ?? null,
        bannerFlagsEn,
        bannerFlagsTh,
      }
    }
    case EventType.SAKURA: {
      const themeAssets = SAKURA_ASSETS[nameKey]
      const bannerFlagsEn =
        nameKey === 'blue'
          ? ((themeAssets as typeof SAKURA_ASSETS.blue)?.bannerFlagsEn ?? null)
          : null
      const bannerFlagsTh =
        nameKey === 'blue'
          ? ((themeAssets as typeof SAKURA_ASSETS.blue)?.bannerFlagsTh ?? null)
          : null
      // const cardFlagBanner =
      //   nameKey === 'gold'
      //     ? ((themeAssets as typeof SAKURA_ASSETS.gold)?.decorations
      //         ?.flagBanner ?? null)
      //     : null
      const cardFlagBanner = null
      const cardLantern =
        nameKey === 'blue'
          ? ((themeAssets as typeof SAKURA_ASSETS.blue)?.decorations
              ?.cardLantern ?? null)
          : null

      return {
        logo: themeAssets?.logo?.[modeKey] ?? null,
        loadingBackground: themeAssets?.loadingAssets?.backgrounds?.bg1 ?? null,
        loadingBackgrounds: themeAssets?.loadingAssets?.backgrounds ?? null,
        ribbon: null,
        abstractBorder: themeAssets?.decorations?.abstractBorder ?? null,
        cardLantern,
        cardFlagBanner,
        chineseKnot: null,
        webLeft: null,
        webRight: null,
        models: themeAssets?.models ?? null,
        bannerFlagsEn,
        bannerFlagsTh,
      }
    }
    case EventType.SONGKRAN: {
      const themeAssets = SONGKRAN_ASSETS[nameKey]
      const bannerFlagsEn =
        nameKey === 'blue'
          ? ((themeAssets as typeof SONGKRAN_ASSETS.blue)?.bannerFlagsEn ??
            null)
          : null
      const bannerFlagsTh =
        nameKey === 'blue'
          ? ((themeAssets as typeof SONGKRAN_ASSETS.blue)?.bannerFlagsTh ??
            null)
          : null
      const cardFlagBanner =
        nameKey === 'gold'
          ? ((themeAssets as typeof SONGKRAN_ASSETS.gold)?.decorations
              ?.flagBanner ?? null)
          : null

      return {
        logo: themeAssets?.logo?.[modeKey] ?? null,
        loadingBackground: themeAssets?.loadingAssets?.backgrounds?.bg1 ?? null,
        loadingBackgrounds: themeAssets?.loadingAssets?.backgrounds ?? null,
        ribbon: null,
        abstractBorder: null,
        cardLantern: null,
        cardFlagBanner,
        chineseKnot: null,
        webLeft: null,
        webRight: null,
        models: themeAssets?.models ?? null,
        bannerFlagsEn,
        bannerFlagsTh,
      }
    }
    case EventType.LOY_KRATHONG: {
      const themeAssets = LOY_KRATHONG_ASSETS[nameKey]
      const bannerFlagsEn =
        nameKey === 'blue'
          ? ((themeAssets as typeof LOY_KRATHONG_ASSETS.blue)?.bannerFlagsEn ??
            null)
          : null
      const bannerFlagsTh =
        nameKey === 'blue'
          ? ((themeAssets as typeof LOY_KRATHONG_ASSETS.blue)?.bannerFlagsTh ??
            null)
          : null

      return {
        logo: themeAssets?.logo?.[modeKey] ?? null,
        loadingBackground: themeAssets?.loadingAssets?.backgrounds?.bg1 ?? null,
        loadingBackgrounds: themeAssets?.loadingAssets?.backgrounds ?? null,
        ribbon: null,
        abstractBorder: themeAssets?.decorations?.palaceDecor ?? null,
        cardLantern: themeAssets?.decorations?.cardDecor ?? null,
        cardFlagBanner: null,
        chineseKnot: null,
        webLeft: null,
        webRight: null,
        models: themeAssets?.models ?? null,
        bannerFlagsEn,
        bannerFlagsTh,
      }
    }
    case EventType.CHINESE_NEW_YEAR: {
      const themeAssets = CHINESE_NEW_YEAR_ASSETS[nameKey]
      // Banner flags, card lantern, and chinese knot only available for blue theme
      const bannerFlagsEn =
        nameKey === 'blue'
          ? ((themeAssets as typeof CHINESE_NEW_YEAR_ASSETS.blue)
              ?.bannerFlagsEn ?? null)
          : null
      const bannerFlagsTh =
        nameKey === 'blue'
          ? ((themeAssets as typeof CHINESE_NEW_YEAR_ASSETS.blue)
              ?.bannerFlagsTh ?? null)
          : null
      const cardLantern =
        nameKey === 'blue'
          ? ((themeAssets as typeof CHINESE_NEW_YEAR_ASSETS.blue)?.decorations
              ?.cardLantern ?? null)
          : null
      const chineseKnot =
        nameKey === 'blue'
          ? ((themeAssets as typeof CHINESE_NEW_YEAR_ASSETS.blue)?.decorations
              ?.chineseKnot ?? null)
          : null
      return {
        logo: themeAssets?.logo?.[modeKey] ?? null,
        loadingBackground: themeAssets?.loadingAssets?.backgrounds?.bg1 ?? null,
        loadingBackgrounds: themeAssets?.loadingAssets?.backgrounds ?? null,
        ribbon: null, // CNY doesn't use ribbon decoration
        abstractBorder: themeAssets?.decorations?.abstractBorder ?? null,
        cardLantern,
        cardFlagBanner: null,
        chineseKnot,
        webLeft: null,
        webRight: null,
        models: themeAssets?.models ?? null,
        bannerFlagsEn,
        bannerFlagsTh,
      }
    }
    default:
      return {
        logo: null,
        loadingBackground: null,
        loadingBackgrounds: null,
        ribbon: null,
        abstractBorder: null,
        cardLantern: null,
        cardFlagBanner: null,
        chineseKnot: null,
        webLeft: null,
        webRight: null,
        models: null,
        bannerFlagsEn: null,
        bannerFlagsTh: null,
      }
  }
}

export const useEventAssets = () => {
  const { theme } = useTheme()
  const event = useCurrentEvent()
  const themeName = getThemeName(theme)
  const themeMode = getThemeMode(theme)

  const eventAssets = useMemo(() => {
    return getEventAssets(event, themeName, themeMode)
  }, [event, themeName, themeMode])

  return {
    event,
    isEventActive: event !== null,
    eventAssets,
  }
}
